import { useCallback, useEffect, useState } from "react";
import PageContainer from "../components/pageContainer/PageContainer";
import SettingsLayout from "../components/settings/SettingsLayout";
import SettingsPageHeader from "../components/settings/SettingsPageHeader";
import SettingsEntityRow from "../components/settings/SettingsEntityRow";
import StyledInput from "../components/styledInput/StyledInput";
import Button from "../components/button";
import useModal from "../components/modal/useModal";
import UseContext from "../hooks/useContext";
import { Pencil, Plus, ChevronDown } from "../assets/icons";
import useTitle from "../hooks/TitleManager";
import useIntegrations from "../hooks/useIntegrations";
import ApiDatabase from "../server";
import { SettingsSectorsListGhost } from "../components/ghost/SettingsAndEmployeesGhost";
import SettingsHelpBanner from "../components/settings/SettingsHelpBanner";
import EmptyState from "../components/EmptyState/EmptyState";

/** En-tête accordéon : titres sans cadre, seul le chevron est en Button SECONDARY. */
const AccordionSectionHeader = ({ title, description, isOpen, onToggle }) => (
  <div className="flex items-start justify-between gap-3 px-4">
    <button
      type="button"
      className="min-w-0 flex-1 rounded-md text-left outline-none ring-gray-200 focus-visible:ring-2"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {description ? (
        <p className="mt-1 text-[14px] leading-snug text-gray-500">{description}</p>
      ) : null}
    </button>
    <Button
      color="SECONDARY"
      size="ICON"
      onClick={onToggle}
      title="Replie ou déplie la section"
    >
      <span
        className={`inline-flex transition-transform duration-200 ${
          isOpen ? "rotate-180" : ""
        }`}
      >
        <ChevronDown wh={20} color="#374151" />
      </span>
    </Button>
  </div>
);

const SectorRow = ({ sector, employees, readOnly, onChangeUsers, onEdit }) => (
  <SettingsEntityRow
    label={sector.name}
    middleSlot={
      <StyledInput
        type="selectMultipleCheckbox"
        isSearchable={true}
        value={`${sector.userIds?.length || 0} ${
          (sector.userIds?.length || 0) === 1 ? "utilisateur" : "utilisateurs"
        }`}
        list={(employees || []).map((employee) => ({
          value: employee._id,
          label: `${employee.firstname} ${employee.lastname}`.trim(),
          checked: (sector.userIds || []).some(
            (id) => String(id) === String(employee._id)
          ),
        }))}
        onChange={(updated) => onChangeUsers(sector, updated)}
      />
    }
    actionsSlot={
      !readOnly ? (
        <Button color="ICON" size="ICON" onClick={() => onEdit(sector)}>
          <Pencil wh={20} color="#374151" />
        </Button>
      ) : null
    }
  />
);

const SettingsSectors = () => {
  useTitle({ title: "Paramètres - Secteurs" });

  const [sectors, setSectors] = useState({ workfully: [], peopulse: [], jobbiz: [] });
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [accordionOpen, setAccordionOpen] = useState({
    workfully: true,
    peopulse: true,
    jobbiz: true,
  });
  const { toggle } = useModal();
  const { modalDataReturn, setModalData, setModalDataReturn } = UseContext();
  const integrations = useIntegrations();

  const refresh = useCallback(() => {
    setIsLoading(true);
    ApiDatabase.getSectors(
      {},
      (data) => {
        setSectors(data?.grouped || { workfully: [], peopulse: [], jobbiz: [] });
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
  }, []);

  useEffect(() => {
    refresh();
    ApiDatabase.getCompaniesInfosEmployees(
      {},
      (data) => {
        const flat = [];
        const seen = new Set();
        for (const company of data?.companies || []) {
          for (const employee of company.employees || []) {
            const id = String(employee._id);
            if (!seen.has(id)) {
              seen.add(id);
              flat.push(employee);
            }
          }
        }
        setEmployees(flat);
      },
      () => {}
    );
  }, [refresh]);

  useEffect(() => {
    if (!modalDataReturn?.sectors) return;
    setModalDataReturn({});
    refresh();
  }, [modalDataReturn, setModalDataReturn, refresh]);

  const handleOpenCreate = () => {
    setModalData({
      employees,
    });
    toggle("create_sector");
  };

  const handleOpenEdit = (sector) => {
    setModalData({
      sector,
      onDeleteRequest: (s) => {
        setTimeout(() => {
          setModalData({
            sector: s,
          });
          toggle("delete_sector");
        }, 0);
      },
    });
    toggle("edit_sector");
  };

  const patchSectorUserIdsInState = useCallback((sectorId, nextUserIds) => {
    setSectors((prev) => {
      const sources = ["workfully", "peopulse", "jobbiz"];
      let draft = null;
      for (const key of sources) {
        const list = prev[key];
        if (!Array.isArray(list)) continue;
        const idx = list.findIndex((s) => String(s._id) === String(sectorId));
        if (idx === -1) continue;
        if (!draft) draft = { ...prev };
        const copy = [...draft[key]];
        copy[idx] = { ...copy[idx], userIds: [...nextUserIds] };
        draft[key] = copy;
      }
      return draft || prev;
    });
  }, []);

  const handleChangeUsers = (sector, updated) => {
    const userIds = updated.filter((opt) => opt.checked).map((opt) => opt.value);
    const previousUserIds =
      sector.userIds != null ? [...sector.userIds] : [];

    patchSectorUserIdsInState(sector._id, userIds);

    ApiDatabase.putUpdateSector(
      { id: sector._id, userIds },
      (data) => {
        const saved = data?.sector?.userIds;
        if (saved !== undefined) {
          patchSectorUserIdsInState(sector._id, saved);
        }
      },
      () => {
        patchSectorUserIdsInState(sector._id, previousUserIds);
      },
    );
  };

  const integrationLabel = (source) =>
    source === "peopulse" ? "Peopulse" : source === "jobbiz" ? "Jobbiz" : "Workfully";

  /** Sous-titre accordéon : « N récupérée(s) depuis Peopulse/Jobbiz » */
  const integrationRecuperesLine = (count, source) => {
    const brand = source === "peopulse" ? "Peopulse" : "Jobbiz";
    if (count === 0) return `0 récupéré depuis ${brand}`;
    if (count === 1) return `1 récupérée depuis ${brand}`;
    return `${count} récupérées depuis ${brand}`;
  };

  /** Sous-titre accordéon Workfully : « N créé(s) depuis Workfully » */
  const workfullyCreesLine = (count) =>
    count <= 1 ? `${count} créé depuis Workfully` : `${count} créés depuis Workfully`;

  return (
    <PageContainer type="settings_sectors">
      <div className="flex w-full max-w-[1122px] flex-col gap-6">
        <div className="grid grid-cols-1 gap-8 pt-6 lg:grid-cols-[minmax(0,1fr)_minmax(400px,440px)] lg:items-stretch">
          <SettingsLayout withTopPadding={false} innerScroll={false}>
            <div className="flex flex-col gap-6">
          <SettingsPageHeader
            title="Secteurs"
            action={
              <Button color="PRIMARY" size="SM" onClick={handleOpenCreate}>
                <div className="flex items-center gap-1">
                  <Plus wh={20} color="#FFFFFF" />
                  Créer
                </div>
              </Button>
            }
          />

          {/* {integrations.hasAny && (
            <Banner type="info" hideIcon>
              <div className="flex flex-col gap-1">
                <div className="font-semibold text-[#111827]">Intégrations</div>
                <div>
                  Les secteurs sont automatiquement importés depuis vos
                  intégrations. Si vous utilisez uniquement vos intégrations,
                  inutile d'en créer manuellement.
                </div>
              </div>
            </Banner>
          )} */}

          {isLoading ? (
            <section className="flex flex-col pt-6">
              <SettingsSectorsListGhost />
            </section>
          ) : (sectors.workfully || []).length === 0 ? (
            <div className="flex flex-col pt-6">
              <EmptyState type="settings_sectors" />
            </div>
          ) : (
            <section className="flex flex-col gap-3 pt-6">
              <AccordionSectionHeader
                title="Workfully"
                description={workfullyCreesLine((sectors.workfully || []).length)}
                isOpen={accordionOpen.workfully}
                onToggle={() =>
                  setAccordionOpen((prev) => ({
                    ...prev,
                    workfully: !prev.workfully,
                  }))
                }
              />
              {accordionOpen.workfully && (
                <div className="flex flex-col gap-0">
                  {(sectors.workfully || []).map((sector) => (
                    <SectorRow
                      key={sector._id}
                      sector={sector}
                      employees={employees}
                      readOnly={false}
                      onChangeUsers={handleChangeUsers}
                      onEdit={handleOpenEdit}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Intégrations (accordéons) */}
          {["peopulse", "jobbiz"].map((source) => {
            if (!integrations[source]) return null;
            const list = sectors[source] || [];
            if (list.length === 0) return null;
            const isOpen = accordionOpen[source];
            return (
              <section key={source} className="flex flex-col gap-3 pt-6">
                <AccordionSectionHeader
                  title={integrationLabel(source)}
                  description={integrationRecuperesLine(list.length, source)}
                  isOpen={isOpen}
                  onToggle={() =>
                    setAccordionOpen((prev) => ({
                      ...prev,
                      [source]: !prev[source],
                    }))
                  }
                />
                {isOpen && (
                  <div className="flex flex-col gap-0">
                    {list.map((sector) => (
                      <SectorRow
                        key={sector._id}
                        sector={sector}
                        employees={employees}
                        readOnly
                        onChangeUsers={handleChangeUsers}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
            </div>
          </SettingsLayout>

          <aside className="hidden min-w-0 lg:block">
            <div>
              <SettingsHelpBanner
                title="Qu'est-ce qu'un secteur ?"
                description="Les secteurs définissent les périmètres d'intervention. Chaque utilisateur ne voit que les secteurs auxquels il est rattaché. Les administrateurs ont une vue globale."
              />
            </div>
          </aside>
        </div>
      </div>
    </PageContainer>
  );
};

export default SettingsSectors;
